import React, { useEffect } from 'react';
import Drawer from '../../ui/Drawer';
import Badge from '../../ui/Badge';
import { useOfferHistory } from '../hooks/useOfferHistory';
import { formatPrice, relativeTime } from '../utils';
import './OfferHistoryDrawer.css';

const ACTION_LABELS = {
  submitted: 'Submitted',
  countered: 'Countered',
  counter_buyer: 'Counter from buyer',
  counter_seller: 'Counter from seller',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
  closed: 'Closed',
};

function entryLabel(entry) {
  if (entry.action && ACTION_LABELS[entry.action]) return ACTION_LABELS[entry.action];
  if (entry.status && ACTION_LABELS[entry.status]) return ACTION_LABELS[entry.status];
  if (entry.action) return entry.action.replace(/_/g, ' ');
  if (entry.status) return entry.status.replace(/_/g, ' ');
  return 'Update';
}

function entryPrice(entry) {
  return entry.price ?? entry.amount ?? entry.offer_price ?? null;
}

/**
 * OfferHistoryDrawer — slide-over showing the timeline of revisions for one offer.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - offerId: number | null
 *  - offer?: object — optional summary info for the header card
 */
export default function OfferHistoryDrawer({ open, onClose, offerId, offer }) {
  const { history, loading, error, fetchHistory } = useOfferHistory();

  useEffect(() => {
    if (open && offerId) {
      fetchHistory(offerId);
    }
  }, [open, offerId, fetchHistory]);

  const showEmpty = !loading && !error && history.length === 0;

  return (
    <Drawer open={open} onClose={onClose} title="Offer History">
      <div className="offer-history-drawer">
        {offer && (
          <section className="offer-history-drawer__summary">
            {offer.property_address && (
              <p className="offer-history-drawer__address">{offer.property_address}</p>
            )}
            <div className="offer-history-drawer__summary-row">
              {offer.status && <Badge variant="status" value={offer.status} />}
              {entryPrice(offer) != null && (
                <span className="offer-history-drawer__price">
                  {formatPrice(entryPrice(offer))}
                </span>
              )}
            </div>
          </section>
        )}

        <div
          className="offer-history-drawer__content"
          aria-busy={loading || undefined}
        >
          {error && (
            <div role="alert" className="offer-history-drawer__error">
              <p>Couldn't load history. Try again.</p>
              <button
                type="button"
                className="offer-history-drawer__btn"
                onClick={() => offerId && fetchHistory(offerId)}
              >
                Retry
              </button>
            </div>
          )}

          {loading && history.length === 0 && !error && (
            <ul className="offer-history-drawer__skeletons">
              {[0, 1, 2].map((i) => (
                <li key={i} className="offer-history-skeleton" />
              ))}
            </ul>
          )}

          {showEmpty && (
            <div className="offer-history-drawer__empty">
              <p>No history yet.</p>
            </div>
          )}

          {!showEmpty && !error && history.length > 0 && (
            <ol className="offer-history-drawer__timeline">
              {history.map((entry, idx) => {
                const price = entryPrice(entry);
                return (
                  <li key={entry.id ?? idx} className="offer-history-drawer__entry">
                    <span
                      className="offer-history-drawer__marker"
                      aria-hidden="true"
                    >
                      {idx + 1}
                    </span>
                    <div className="offer-history-drawer__entry-body">
                      <p className="offer-history-drawer__entry-title">
                        {entryLabel(entry)}
                      </p>
                      <div className="offer-history-drawer__entry-meta">
                        {price != null && (
                          <span className="offer-history-drawer__entry-price">
                            {formatPrice(price)}
                          </span>
                        )}
                        {entry.created_at && (
                          <time
                            className="offer-history-drawer__entry-time"
                            dateTime={entry.created_at}
                          >
                            {relativeTime(entry.created_at)}
                          </time>
                        )}
                      </div>
                      {entry.changes_description && (
                        <p className="offer-history-drawer__entry-desc">
                          {entry.changes_description}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </Drawer>
  );
}
